import { useState, useEffect } from 'react';
import { getIncidents, updateIncident } from '../api';
import { useNavigate } from 'react-router-dom';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { AlertTriangle, CheckCircle2, Clock, FileText, MonitorCog, X } from 'lucide-react';

const CLASSIFICATION_COLORS = {
  CRITICAL:      { bg: '#FEE2E2', text: '#991B1B', accent: '#DC2626' },
  MAJOR:         { bg: '#FFFBEB', text: '#92400E', accent: '#D97706' },
  MINOR:         { bg: '#FEFCE8', text: '#854D0E', accent: '#CA8A04' },
  INFORMATIONAL: { bg: '#EFF6FF', text: '#1E40AF', accent: '#3B82F6' },
};

const RECOVERY_COLORS = {
  ONGOING:    { bg: '#FEE2E2', text: '#991B1B', accent: '#DC2626' },
  MONITORING: { bg: '#FFFBEB', text: '#92400E', accent: '#D97706' },
  RESOLVED:   { bg: '#F0FDF4', text: '#166534', accent: '#16A34A' },
};

const Badge = ({ label, bg, text }) => (
  <span style={{ background: bg, color: text, padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
    {label}
  </span>
);

const DeadlineRow = ({ label, filed, deadline, isMobile, theme }) => {
  const isOverdue = deadline && !filed && new Date(deadline) < new Date();
  return (
    <div style={{ flex: 1, background: theme.bgInput, borderRadius: 8, padding: isMobile ? '10px 12px' : '12px 14px', border: `1px solid ${theme.border}`, minWidth: isMobile ? '100%' : 180 }}>
      <p style={{ margin: 0, fontSize: 11, color: theme.textSecondary, fontWeight: 600 }}>{label}</p>
      {filed ? (
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#166534', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
          <CheckCircle2 size={13} />
          Filed {new Date(filed).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      ) : (
        <p style={{ margin: '4px 0 0', fontSize: 12, fontWeight: 600, color: isOverdue ? '#DC2626' : '#92400E', display: 'flex', alignItems: 'center', gap: 5 }}>
          {isOverdue ? <AlertTriangle size={13} /> : <Clock size={13} />}
          {isOverdue ? 'Overdue ' : 'Due '}
          {deadline ? new Date(deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'date unknown'}
        </p>
      )}
    </div>
  );
};

const Incidents = () => {
  const navigate   = useNavigate();
  const isMobile   = useMobile();
  const { theme }  = useTheme();

  const [incidents,      setIncidents]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filterStatus,   setFilterStatus]   = useState('ONGOING');
  const [resolveId,      setResolveId]      = useState(null);
  const [resolveForm,    setResolveForm]    = useState({ root_cause: '', preventive_measures: '', recovery_status: 'RESOLVED' });
  const [resolving,      setResolving]      = useState(false);
  const [resolveMsg,     setResolveMsg]     = useState(null);

  useEffect(() => {
    getIncidents()
      .then(res => setIncidents(res.data.incidents || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const ongoingCount    = incidents.filter(i => i.recovery_status === 'ONGOING').length;
  const monitoringCount = incidents.filter(i => i.recovery_status === 'MONITORING').length;
  const resolvedCount   = incidents.filter(i => i.recovery_status === 'RESOLVED').length;
  const cssfCount       = incidents.filter(i => i.cssf_notification_required).length;
  const filtered        = filterStatus === 'ALL' ? incidents : incidents.filter(i => i.recovery_status === filterStatus);

  const handleResolve = async () => {
    if (!resolveForm.root_cause || resolveForm.root_cause.length < 20) {
      setResolveMsg({ type: 'error', text: 'Root cause must be at least 20 characters.' });
      return;
    }
    setResolving(true); setResolveMsg(null);
    try {
      await updateIncident(resolveId, {
        recovery_status:     resolveForm.recovery_status,
        root_cause:          resolveForm.root_cause,
        preventive_measures: resolveForm.preventive_measures,
        resolved_at:         resolveForm.recovery_status === 'RESOLVED' ? true : undefined,
      });
      const res = await getIncidents();
      setIncidents(res.data.incidents || []);
      setResolveId(null);
      setResolveForm({ root_cause: '', preventive_measures: '', recovery_status: 'RESOLVED' });
    } catch (err) {
      setResolveMsg({ type: 'error', text: err.response?.data?.error || 'Update failed.' });
    } finally { setResolving(false); }
  };

  const filterButton = (value, label, count) => {
    const active = filterStatus === value;
    return (
      <button key={value} onClick={() => setFilterStatus(value)} style={{
        padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
        background: active ? '#3B82F6' : theme.bgInput,
        color: active ? 'white' : theme.textSecondary,
        border: `1px solid ${active ? '#3B82F6' : theme.border}`,
      }}>
        {label} ({count})
      </button>
    );
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${theme.border}`, fontSize: 13,
    color: theme.textPrimary, background: theme.bgInput,
    outline: 'none', resize: 'vertical', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <div>
      {/* Back */}
      <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textSecondary, fontWeight: 600, fontSize: 12, marginBottom: 16, padding: 0 }}>
        Back to Dashboard
      </button>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.textPrimary }}>DORA Incident Management</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: ongoingCount > 0 ? '#DC2626' : theme.textSecondary, fontWeight: ongoingCount > 0 ? 600 : 400 }}>
          {ongoingCount > 0 ? `${ongoingCount} ongoing incident${ongoingCount > 1 ? 's' : ''} - CSSF reporting required` : 'All systems operational - no ongoing incidents'}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total',         value: incidents.length, color: '#3B82F6', icon: FileText },
          { label: 'Ongoing',       value: ongoingCount,     color: '#DC2626', icon: AlertTriangle },
          { label: 'Monitoring',    value: monitoringCount,  color: '#D97706', icon: Clock },
          { label: 'Resolved',      value: resolvedCount,    color: '#16A34A', icon: CheckCircle2 },
          { label: 'CSSF Required', value: cssfCount,        color: '#8B5CF6', icon: MonitorCog },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} style={{ background: theme.bgCard, borderRadius: 10, padding: '16px 20px', border: `1px solid ${theme.border}`, boxShadow: theme.shadowSm, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={18} color={s.color} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.textPrimary, lineHeight: 1 }}>{s.value}</p>
                <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary, fontWeight: 600 }}>{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ongoing warning */}
      {ongoingCount > 0 && (
        <div style={{ background: '#FEF2F2', borderRadius: 8, padding: '12px 16px', marginBottom: 16, border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={15} color="#DC2626" />
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
            {ongoingCount} incident{ongoingCount > 1 ? 's' : ''} currently ongoing - review reporting deadlines
          </p>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: theme.bgCard, borderRadius: 10, padding: '12px 14px', marginBottom: 14, border: `1px solid ${theme.border}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          ['ONGOING',    'Ongoing',    ongoingCount],
          ['MONITORING', 'Monitoring', monitoringCount],
          ['RESOLVED',   'Resolved',   resolvedCount],
          ['ALL',        'All',        incidents.length],
        ].map(([value, label, count]) => filterButton(value, label, count))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: theme.textTertiary, whiteSpace: 'nowrap' }}>
          {filtered.length} of {incidents.length}
        </span>
      </div>

      {/* Incident list */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14 }}>Loading incidents...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14, background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}` }}>
          No incidents recorded.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(incident => {
            const recoveryMeta       = RECOVERY_COLORS[incident.recovery_status]     || RECOVERY_COLORS.RESOLVED;
            const classificationMeta = CLASSIFICATION_COLORS[incident.classification] || CLASSIFICATION_COLORS.INFORMATIONAL;
            return (
              <div key={incident.id} style={{
                background: theme.bgCard, borderRadius: 10,
                padding: isMobile ? '12px 14px' : '14px 18px',
                border: `1px solid ${theme.border}`,
                borderLeft: `3px solid ${recoveryMeta.accent}`,
                boxShadow: theme.shadowSm, transition: 'all 0.15s',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{ background: '#EFF6FF', color: '#1D4ED8', padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {incident.incident_reference}
                      </span>
                      {incident.classification && <Badge label={incident.classification} bg={classificationMeta.bg} text={classificationMeta.text} />}
                      <Badge label={incident.recovery_status?.replace(/_/g, ' ') || 'UNKNOWN'} bg={recoveryMeta.bg} text={recoveryMeta.text} />
                      {incident.cssf_notification_required && <Badge label="CSSF REQUIRED" bg="#FFFBEB" text="#92400E" />}
                    </div>

                    {incident.description && (
                      <p style={{ margin: 0, fontSize: isMobile ? 13 : 14, color: theme.textPrimary, fontWeight: 600, lineHeight: 1.4 }}>
                        {incident.description}
                      </p>
                    )}

                    {incident.affected_systems?.length > 0 && (
                      <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
                        {incident.affected_systems.map(system => (
                          <span key={system} style={{ background: '#FEE2E2', color: '#991B1B', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>
                            {system}
                          </span>
                        ))}
                      </div>
                    )}

                    {incident.cssf_notification_required && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap' }}>
                        <DeadlineRow label="Initial (T+4hr)"       filed={incident.initial_notification_filed} deadline={incident.initial_notification_deadline}  isMobile={isMobile} theme={theme} />
                        <DeadlineRow label="Intermediate (T+72hr)" filed={incident.intermediate_report_filed}  deadline={incident.intermediate_report_deadline}    isMobile={isMobile} theme={theme} />
                        <DeadlineRow label="Final Report (T+1mo)"  filed={incident.final_report_filed}         deadline={incident.final_report_deadline}           isMobile={isMobile} theme={theme} />
                      </div>
                    )}

                    {incident.root_cause && (
                      <div style={{ marginTop: 10, padding: '10px 12px', background: theme.bgBase, borderRadius: 8, border: `1px solid ${theme.border}` }}>
                        <p style={{ margin: '0 0 3px', fontSize: 10, fontWeight: 700, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Root Cause</p>
                        <p style={{ margin: 0, fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>{incident.root_cause}</p>
                      </div>
                    )}

                    {incident.cssf_submission_ref && (
                      <p style={{ margin: '10px 0 0', fontSize: 12, color: theme.textSecondary, borderTop: `1px solid ${theme.border}`, paddingTop: 10 }}>
                        CSSF Reference:{' '}
                        <span style={{ background: '#EFF6FF', color: '#1D4ED8', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                          {incident.cssf_submission_ref}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Right side */}
                  <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    {incident.recovery_status !== 'RESOLVED' && (
                      <button onClick={() => { setResolveId(incident.id); setResolveMsg(null); setResolveForm({ root_cause: '', preventive_measures: '', recovery_status: 'RESOLVED' }); }} style={{
                        background: '#3B82F6', color: 'white', border: 'none',
                        borderRadius: 7, padding: '6px 12px', fontSize: 11,
                        fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>
                        Update Status
                      </button>
                    )}
                    <p style={{ margin: 0, fontSize: 11, color: theme.textSecondary, whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {incident.detected_at ? new Date(incident.detected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date unknown'}
                    </p>
                    {!isMobile && incident.detected_at && (
                      <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary }}>
                        {new Date(incident.detected_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ color: theme.textTertiary, fontSize: 12, marginTop: 14, textAlign: 'center' }}>
        Showing {filtered.length} of {incidents.length} incidents
      </p>

      {/* Resolve modal */}
      {resolveId && (
        <div style={{ position: 'fixed', inset: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: theme.bgCard, borderRadius: 12, padding: '28px', width: '100%', maxWidth: 480, boxShadow: theme.shadowLg, boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>Update Incident Status</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>Document the resolution for DORA compliance records.</p>
              </div>
              <button onClick={() => { setResolveId(null); setResolveMsg(null); }} style={{ background: theme.bgBadge, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 8, display: 'flex', color: theme.textSecondary }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textSecondary, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</label>
              <select value={resolveForm.recovery_status} onChange={e => setResolveForm(f => ({ ...f, recovery_status: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="RESOLVED">Resolved</option>
                <option value="MONITORING">Monitoring</option>
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textSecondary, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Root Cause <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <textarea value={resolveForm.root_cause} onChange={e => setResolveForm(f => ({ ...f, root_cause: e.target.value }))}
                placeholder="Describe the root cause of the incident (min 20 characters)..."
                rows={3} style={inputStyle} />
              <p style={{ margin: '3px 0 0', fontSize: 11, color: resolveForm.root_cause.length >= 20 ? '#16A34A' : theme.textTertiary }}>
                {resolveForm.root_cause.length} / 20 minimum
              </p>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textSecondary, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Preventive Measures</label>
              <textarea value={resolveForm.preventive_measures} onChange={e => setResolveForm(f => ({ ...f, preventive_measures: e.target.value }))}
                placeholder="What steps were taken to prevent recurrence?"
                rows={3} style={inputStyle} />
            </div>

            {resolveMsg && (
              <div style={{ background: '#FEF2F2', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
                {resolveMsg.text}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setResolveId(null); setResolveMsg(null); }} style={{
                background: theme.bgBadge, color: theme.textSecondary, border: `1px solid ${theme.border}`,
                borderRadius: 8, padding: '9px 20px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}>
                Cancel
              </button>
              <button onClick={handleResolve} disabled={resolving || resolveForm.root_cause.length < 20} style={{
                background: resolveForm.root_cause.length < 20 ? theme.bgBadge : '#3B82F6',
                color: resolveForm.root_cause.length < 20 ? theme.textTertiary : 'white',
                border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13,
                cursor: resolving ? 'not-allowed' : 'pointer',
              }}>
                {resolving ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Incidents;